import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles } from "lucide-react";

interface CreateDashboardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (name: string, description: string, prompt?: string) => void;
  saving?: boolean;
}

const CreateDashboardDialog = ({ open, onOpenChange, onCreated, saving }: CreateDashboardDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");

  const handleSubmit = () => {
    if (!name.trim()) return;
    onCreated(name.trim(), description.trim(), prompt.trim() || undefined);
    setName("");
    setDescription("");
    setPrompt("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Dashboard</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales Overview" autoFocus />
          </div>
          <div className="grid gap-1.5">
            <Label className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              Generate with AI (optional)
            </Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder='Describe the widgets you want, e.g. "Show monthly revenue trend, top 10 customers by spend, and order status breakdown"'
              rows={3}
            />
            <p className="text-[11px] text-muted-foreground">
              Provide a prompt to auto-generate widgets with SQL queries from your database schema.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || saving}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {prompt.trim() ? (
              <>
                <Sparkles className="mr-1 h-3.5 w-3.5" />
                Generate
              </>
            ) : (
              "Create"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDashboardDialog;
